/**
 * ExamReview.tsx
 * Review page shown after exam submission (mandatory for free exams)
 * Uses VSAT Theme System with dark/light mode support and i18n
 */

import { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Star, MessageSquare, Send, Loader2 } from 'lucide-react';
import { Button } from '@vsat/shared/components';
import { Card, CardContent, CardHeader, CardTitle } from '@vsat/shared/components';
import { Textarea } from '@vsat/shared/components';
import { apiService } from '@vsat/shared/lib/api';
import { toast } from 'sonner';

interface Props {
  examId: string;
  examTitle: string;
  resultId?: string;
  isRequired?: boolean; // For free exams, review is mandatory
  onSubmit: (review: { rating: number; comment?: string }) => void;
  onSkip?: () => void; // Optional skip for paid exams
}

export default function ExamReview({
  examId,
  examTitle,
  resultId,
  isRequired = false,
  onSubmit,
  onSkip,
}: Props) {
  const { t } = useTranslation();
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleStarClick = (value: number) => {
    setRating(value);
  };

  const handleStarHover = (value: number) => {
    setHoveredRating(value);
  };

  const handleStarLeave = () => {
    setHoveredRating(0);
  };

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error(t('examReview.errors.ratingRequired'));
      return;
    }

    setIsSubmitting(true);
    try {
      await apiService.submitExamReview(examId, {
        rating,
        comment: comment.trim() || undefined,
        resultId,
      });

      toast.success(t('examReview.errors.submitSuccess'));
      onSubmit({ rating, comment: comment.trim() });
    } catch (error: any) {
      console.error('Error submitting review:', error);

      // If review already exists (409 Conflict), treat it as success and proceed
      const statusCode = error.response?.status || error.statusCode || error.status;
      const errorMessage = error.message || error.response?.data?.message || '';

      if (statusCode === 409 || errorMessage.toLowerCase().includes('already reviewed')) {
        console.log('Review already exists, proceeding to results page');
        // Don't show error toast, just proceed to results
        onSubmit({ rating, comment: comment.trim() });
      } else {
        toast.error(errorMessage || t('examReview.errors.submitError'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const displayRating = hoveredRating || rating;

  const getRatingLabel = (value: number): string => {
    return t(`examReview.ratingLabels.${value}`);
  };

  return (
    <div className='min-h-screen bg-gradient-to-br from-primary/5 via-background to-accent/5 flex items-center justify-center p-4'>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        className='w-full max-w-2xl'
      >
        <Card className='group hover:shadow-lg transition-all duration-300 hover:ring-2 hover:ring-primary/20 bg-card text-card-foreground backdrop-blur-sm rounded-2xl shadow-xl border border-border'>
          <CardHeader className='text-center space-y-4 pb-6'>
            <div className='w-16 h-16 bg-gradient-to-br from-primary to-primary/80 rounded-full flex items-center justify-center mx-auto shadow-lg shadow-primary/25'>
              <Star className='h-8 w-8 text-primary-foreground fill-primary-foreground' />
            </div>
            <CardTitle className='text-2xl font-bold text-foreground'>
              {t('examReview.title')}
            </CardTitle>
            <p className='text-sm text-muted-foreground'>
              {isRequired
                ? t('examReview.descriptionRequired')
                : t('examReview.descriptionOptional')}
            </p>
            <p className='text-sm font-medium text-foreground'>{examTitle}</p>
          </CardHeader>

          <CardContent className='space-y-6'>
            {/* Star Rating */}
            <div className='space-y-3'>
              <label className='text-sm font-medium text-foreground flex items-center gap-2'>
                <Star className='h-4 w-4 text-primary' />
                {t('examReview.ratingLabel')}{' '}
                {isRequired && (
                  <span className='text-primary'>{t('examReview.ratingRequired')}</span>
                )}
              </label>
              <div className='flex justify-center gap-2'>
                {[1, 2, 3, 4, 5].map(value => (
                  <button
                    key={value}
                    type='button'
                    onClick={() => handleStarClick(value)}
                    onMouseEnter={() => handleStarHover(value)}
                    onMouseLeave={handleStarLeave}
                    className='focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-lg transition-transform hover:scale-110'
                    disabled={isSubmitting}
                  >
                    <Star
                      className={`h-12 w-12 transition-colors ${
                        value <= displayRating
                          ? 'text-yellow-400 fill-yellow-400 dark:text-yellow-500 dark:fill-yellow-500'
                          : 'text-muted-foreground/40'
                      }`}
                    />
                  </button>
                ))}
              </div>
              {displayRating > 0 && (
                <p className='text-center text-sm text-muted-foreground'>
                  {getRatingLabel(displayRating)}
                </p>
              )}
            </div>

            {/* Comment */}
            <div className='space-y-2'>
              <label className='text-sm font-medium text-foreground flex items-center gap-2'>
                <MessageSquare className='h-4 w-4 text-primary' />
                {t('examReview.commentLabel')}
              </label>
              <Textarea
                value={comment}
                onChange={e => setComment(e.target.value)}
                placeholder={t('examReview.commentPlaceholder')}
                className='min-h-[120px] border border-input rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 bg-background text-foreground'
                disabled={isSubmitting}
                maxLength={500}
              />
              <p className='text-xs text-muted-foreground text-right'>{comment.length}/500</p>
            </div>

            {/* Actions */}
            <div className='flex flex-col sm:flex-row gap-3 pt-4'>
              {onSkip && !isRequired && (
                <Button
                  variant='outline'
                  onClick={onSkip}
                  disabled={isSubmitting}
                  className='flex-1 border border-border hover:bg-secondary hover:text-secondary-foreground rounded-lg transition-all duration-300'
                >
                  {t('examReview.skip')}
                </Button>
              )}
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting || rating === 0}
                className='flex-1 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary text-primary-foreground font-semibold text-base rounded-lg shadow-lg shadow-primary/25 transition-all duration-300'
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                    {t('examReview.submitting')}
                  </>
                ) : (
                  <>
                    <Send className='mr-2 h-4 w-4' />
                    {t('examReview.submit')}
                  </>
                )}
              </Button>
            </div>

            {isRequired && (
              <p className='text-xs text-center text-muted-foreground'>
                {t('examReview.requiredNote')}
              </p>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
